/**
 * Filters font variations based on required actions for resolving entitlements.
 * Standalone port of spectrum/services/filter_variations_service.js.
 */

/**
 * Filters fonts based on the required action preferences for resolving font entitlements.
 *
 * @param {Array} fonts
 * @param {Object} requiredActionFilters
 * @returns {Array}
 */
export function filterFontsByRequiredActions(fonts, requiredActionFilters) {
  var filteredActions = requiredActionFilters || filterRequiredActionsByToggles(fonts, {}).filters;

  return fonts.filter(function (font) {
    var includeUpgrade = filteredActions.upgrade && syncHasAction('upgrade', font);
    return syncHasAction('preview_only', font) || webOnly(font) || (!!syncRequirements(font) && (isEmpty(syncRequirements(font)) || includeUpgrade));
  });
}

/**
 * Returns a set of required action filters based on selected action toggles
 * OR the default action filters for a given list of fonts.
 *
 * @param {Array} fonts
 * @param {Object} requiredActionToggles
 * @returns {{ filters: Object, preferred: String }}
 */
export function filterRequiredActionsByToggles(fonts, requiredActionToggles) {
  var preferredAction;
  var selectedActions = pickTruthy(requiredActionToggles || {});
  var filteredActions = Object.assign({}, defaultActionFilters(fonts), selectedActions);

  if (isEmpty(selectedActions)) {
    preferredAction = (filteredActions.upgrade && 'upgrade') || null;
  } else {
    preferredAction = (selectedActions.upgrade && 'upgrade') || null;
  }

  return {
    filters: filteredActions,
    preferred: preferredAction
  };
}

/**
 * Should an option for showing fonts with an upgrade action be visible?
 * Returns true if we have a mix of fonts available in both the free and paid libraries.
 *
 * @param {Array} fonts
 * @returns {Boolean}
 */
export function hasUpgradeFilterOption(fonts) {
  return hasAvailableFonts(fonts) && hasFontsWithAction('upgrade', fonts);
}

// --- Private helpers ---

function isEmpty(obj) {
  return !obj || Object.keys(obj).length === 0;
}

function pickTruthy(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v));
}

function defaultActionFilters(fonts) {
  return {
    upgrade: hasFontsWithAction('upgrade', fonts) && !hasUpgradeFilterOption(fonts)
  };
}

function hasFontsWithAction(action, fonts) {
  return fonts.some(function (font) {
    return syncHasAction(action, font);
  });
}

function hasAvailableFonts(fonts) {
  return fonts.some(isSyncAvailable) || fonts.some(isWebAvailable);
}

function isSyncAvailable(font) {
  return !!syncRequirements(font) && isEmpty(syncRequirements(font));
}

function isWebAvailable(font) {
  return !!webRequirements(font) && isEmpty(webRequirements(font));
}

function syncRequirements(font) {
  return ((font.font || {}).sync || {}).required_action;
}

function syncHasAction(action, font) {
  return !!syncRequirements(font) && syncRequirements(font).includes(action);
}

function webOnly(font) {
  var onlyWebExists = (!syncRequirements(font) || syncHasAction('preview_only', font)) && !!webRequirements(font);
  var onlyWebAvailable = isWebAvailable(font) && !isSyncAvailable(font);
  return onlyWebExists || onlyWebAvailable;
}

function webRequirements(font) {
  return ((font.font || {}).web || {}).required_action;
}
